'use client';

import React from 'react';
import { Button } from 'antd';
import { EditOutlined, StarFilled } from '@ant-design/icons';
import {
  FaCampground, FaSprayCan, FaTag, FaMapMarkerAlt,
  FaWheelchair, FaShieldAlt, FaPaw, FaChild
} from 'react-icons/fa';
import type { ReviewSummary as ReviewSummaryType } from '../types';

interface ReviewSummaryProps {
  summary: ReviewSummaryType | null;
  onWriteReview: () => void;
}

/** Sub-rating categories with icons (Airbnb-style) */
const SUB_RATING_CATEGORIES: {
  key: keyof ReviewSummaryType['sub_ratings'];
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'facilities', label: 'Facilities', icon: <FaCampground className="text-xl text-gray-500" /> },
  { key: 'cleanliness', label: 'Cleanliness', icon: <FaSprayCan className="text-xl text-gray-500" /> },
  { key: 'value', label: 'Value', icon: <FaTag className="text-xl text-gray-500" /> },
  { key: 'location', label: 'Location', icon: <FaMapMarkerAlt className="text-xl text-gray-500" /> },
  { key: 'accessibility', label: 'Accessibility', icon: <FaWheelchair className="text-xl text-gray-500" /> },
  { key: 'safety', label: 'Safety', icon: <FaShieldAlt className="text-xl text-gray-500" /> },
  { key: 'pet_friendliness', label: 'Pet-friendliness', icon: <FaPaw className="text-xl text-gray-500" /> },
  { key: 'family_friendliness', label: 'Family-friendliness', icon: <FaChild className="text-xl text-gray-500" /> },
];

/** Colour for distribution bars */
const barColour = (star: number): string => {
  const colours: Record<number, string> = {
    5: '#222222',
    4: '#555555',
    3: '#888888',
    2: '#bbbbbb',
    1: '#dddddd',
  };
  return colours[star] ?? '#d1d5db';
};

const ReviewSummaryComponent: React.FC<ReviewSummaryProps> = ({
  summary,
  onWriteReview,
}) => {
  // ── Empty state ─────────────────────────────────────────────
  if (!summary || !summary.total_reviews || summary.average_rating == null) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <StarFilled style={{ fontSize: 28, color: '#f59e0b' }} />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">
          No reviews yet
        </h3>
        <p className="text-sm text-gray-500 mb-6 max-w-xs">
          Be the first to review this campsite! Share your experience and help
          fellow campers.
        </p>
        <Button
          type="primary"
          size="large"
          icon={<EditOutlined />}
          onClick={onWriteReview}
          className="!rounded-lg !font-semibold"
        >
          Write a Review
        </Button>
      </div>
    );
  }

  // ── Compute max count for proportional bars ─────────────────
  const distribution = summary.rating_distribution ?? {};
  const distValues = Object.values(distribution);
  const maxCount = distValues.length > 0
    ? Math.max(...distValues.map((v) => Number(v) || 0), 1)
    : 1;

  // ── Sub-ratings that have data ──────────────────────────────
  const subRatings = summary.sub_ratings ?? {};
  const activeSubRatings = SUB_RATING_CATEGORIES.filter(
    (cat) => subRatings[cat.key] !== null && subRatings[cat.key] !== undefined,
  );

  return (
    <div className="space-y-6">
      {/* ── Top section: Overall rating bar chart + Sub-rating icons ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left: Overall rating distribution */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Overall rating</h3>
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = Number(distribution[star]) || 0;
              const pct = (count / maxCount) * 100;
              return (
                <div key={star} className="flex items-center gap-2 text-sm">
                  <span className="w-3 text-right text-gray-700 font-medium shrink-0 text-xs">
                    {star}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: barColour(star),
                        minWidth: count > 0 ? '4px' : '0px',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Sub-ratings grid with icons (Airbnb-style) */}
        {activeSubRatings.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {activeSubRatings.slice(0, 6).map((cat) => {
              const value = subRatings[cat.key] as number;
              return (
                <div key={cat.key} className="flex flex-col items-start">
                  <span className="text-sm font-medium text-gray-900 mb-0.5">
                    {cat.label}
                  </span>
                  <span className="text-lg font-bold text-gray-900 mb-1">
                    {value.toFixed(1)}
                  </span>
                  {cat.icon}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Sentiment bar (positive / neutral / negative) ────── */}
      {(() => {
        const positive = (Number(distribution[4]) || 0) + (Number(distribution[5]) || 0);
        const neutral = Number(distribution[3]) || 0;
        const negative = (Number(distribution[1]) || 0) + (Number(distribution[2]) || 0);
        const total = positive + neutral + negative;
        if (total === 0) return null;
        return (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Sentiment</h3>
            <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-200">
              {positive > 0 && (
                <div className="bg-green-500 transition-all duration-700" style={{ width: `${(positive / total) * 100}%` }} />
              )}
              {neutral > 0 && (
                <div className="bg-gray-400 transition-all duration-700" style={{ width: `${(neutral / total) * 100}%` }} />
              )}
              {negative > 0 && (
                <div className="bg-red-500 transition-all duration-700" style={{ width: `${(negative / total) * 100}%` }} />
              )}
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
              {positive > 0 && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                  {positive} positive
                </span>
              )}
              {neutral > 0 && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                  {neutral} neutral
                </span>
              )}
              {negative > 0 && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  {negative} negative
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Write a Review CTA */}
      <div className="flex justify-start pt-2">
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={onWriteReview}
          className="!rounded-lg !font-semibold"
        >
          Write a Review
        </Button>
      </div>
    </div>
  );
};

export default ReviewSummaryComponent;
